/**
 * OutcomeDisplay Component
 *
 * Displays the battle outcome (Victory/Defeat/Draw) with appropriate styling
 * and the reason for the outcome.
 *
 * For victories, displays the animated VictoryBanner with particle convergence.
 * For defeats and draws, shows text-based outcome display.
 */

import styles from './VictoryOverlay.module.css';
import { VictoryBanner } from './VictoryBanner';
import type { Outcome, CombatResult } from './types';

export interface OutcomeDisplayProps {
  /** The outcome to display (victory/defeat/draw) */
  outcome: Outcome;
  /** The reason for the outcome */
  reason: CombatResult['reason'];
  /** Winner's display name (required for victory) */
  winnerName?: string;
}

/**
 * Get reason text based on outcome and reason
 */
function getReasonText(outcome: Outcome, reason: CombatResult['reason']): string {
  switch (reason) {
    case 'elimination':
      return outcome === 'defeat' ? 'All units eliminated!' : 'All enemy units eliminated!';
    case 'timeout':
      return 'Time limit reached';
    case 'forfeit':
      return outcome === 'defeat' ? 'You forfeited the battle' : 'Enemy forfeited the battle';
    case 'base_captured':
      return outcome === 'defeat' ? 'Your base was captured!' : 'Enemy base captured!';
    default:
      return '';
  }
}

/**
 * OutcomeDisplay Component
 *
 * Shows the battle outcome with appropriate styling and animation.
 * For victories, shows the animated VictoryBanner with particle convergence.
 * For defeats and draws, shows text-based display.
 */
export function OutcomeDisplay({ outcome, reason, winnerName }: OutcomeDisplayProps) {
  const reasonText = getReasonText(outcome, reason);

  // Show VictoryBanner for victories
  if (outcome === 'victory' && winnerName) {
    return (
      <div className={styles.outcomeDisplay}>
        <VictoryBanner winnerName={winnerName} />
        <p className={styles.reasonText}>{reasonText}</p>
      </div>
    );
  }

  // Text-based display for defeat/draw
  const outcomeText = outcome === 'defeat' ? 'DEFEAT' : 'DRAW';

  return (
    <div className={styles.outcomeDisplay}>
      <h1 className={`${styles.outcomeText} ${styles[outcome]}`}>
        {outcomeText}
      </h1>
      <p className={styles.reasonText}>{reasonText}</p>
    </div>
  );
}

export default OutcomeDisplay;
